import React from 'react';

import {
  Clock3,
  MapPin,
  Ticket,
  Users,
} from 'lucide-react';

import type {
  TournamentViewItem,
} from '../hooks/useTournamentSchedule';


type TournamentCardProps = {
  tournament:
    TournamentViewItem;

  isReserved: boolean;

  isReserving: boolean;

  onReserve:
    () => void;

  onPreviewImage: (
    src: string,
    alt: string,
  ) => void;
};


const TournamentCard:
  React.FC<
    TournamentCardProps
  > = ({
    tournament,
    isReserved,
    isReserving,
    onReserve,
    onPreviewImage,
  }) => {

    const isFull =
      tournament.reservedParticipants >=
      tournament.maxParticipants;


    const remainingSeats =
      Math.max(
        0,
        tournament.maxParticipants -
          tournament.reservedParticipants,
      );


    const reservationRate =
      tournament.maxParticipants >
      0
        ? Math.min(
            100,
            Math.round(
              (
                tournament.reservedParticipants /
                tournament.maxParticipants
              ) *
                100,
            ),
          )
        : 0;


    /**
     * =========================================================
     * 예약 버튼 비활성 조건
     *
     * - 이미 예약 완료
     * - 예약 마감
     * - 현재 예약 처리 중
     * =========================================================
     */
    const isReserveDisabled =
      isReserved ||
      isFull ||
      isReserving;


    /**
     * =========================================================
     * 예약 버튼 문구
     * =========================================================
     */
    const reserveButtonText =
      isReserving
        ? '예약 처리 중...'
        : isReserved
          ? '예약완료'
          : isFull
            ? '예약 마감'
            : '예약하기';


    /**
     * =========================================================
     * 예약 버튼 스타일
     * =========================================================
     */
    const reserveButtonClassName =
      isReserving
        ? `
          cursor-wait
          bg-orange-300
          text-white
        `
        : isReserved
          ? `
            cursor-not-allowed
            bg-green-100
            text-green-700
          `
          : isFull
            ? `
              cursor-not-allowed
              bg-gray-200
              text-gray-500
            `
            : `
              bg-orange-500
              text-white
              hover:bg-orange-600
              active:scale-[0.99]
            `;


    return (
      <article
        className="
          overflow-hidden
          rounded-xl
          border
          border-gray-200
          bg-white
          shadow-sm
        "
      >

        <div
          className="
            grid
            grid-cols-1
            sm:grid-cols-[180px_minmax(0,1fr)]
          "
        >

          {/* =====================================================
              Poster
              ===================================================== */}

          <div
            className="
              relative
              min-h-[220px]
              bg-gray-100
            "
          >

            <img
              src={
                tournament.imageUrl
              }
              alt={
                tournament.title
              }
              className="
                h-full
                w-full
                cursor-zoom-in
                object-cover
              "
              onClick={() =>
                onPreviewImage(
                  tournament.imageUrl,
                  tournament.title,
                )
              }
            />

          </div>


          {/* =====================================================
              Content
              ===================================================== */}

          <div
            className="
              p-4
            "
          >

            <h3
              className="
                text-lg
                font-bold
                text-gray-900
              "
            >
              {tournament.title}
            </h3>


            {/* =================================================
                예약 현황
                ================================================= */}

            <div
              className="
                mt-3
                rounded-lg
                bg-gray-50
                p-3
              "
            >

              <div
                className="
                  flex
                  justify-between
                  text-sm
                "
              >

                <span
                  className="
                    text-gray-500
                  "
                >
                  예약 현황
                </span>


                <strong>
                  {
                    tournament
                      .reservedParticipants
                  }
                  /
                  {
                    tournament
                      .maxParticipants
                  }
                </strong>

              </div>


              <div
                className="
                  mt-2
                  h-1.5
                  overflow-hidden
                  rounded-full
                  bg-gray-200
                "
              >

                <div
                  className={
                    isFull
                      ? 'h-full bg-red-500'
                      : 'h-full bg-orange-500'
                  }
                  style={{
                    width:
                      `${reservationRate}%`,
                  }}
                />

              </div>


              <p
                className="
                  mt-1
                  text-xs
                  text-gray-500
                "
              >
                {isFull
                  ? '예약 마감'
                  : `${remainingSeats}자리 남음`}
              </p>

            </div>


            {/* =================================================
                Tournament Detail
                ================================================= */}

            <div
              className="
                mt-3
                grid
                grid-cols-2
                gap-2
                text-sm
              "
            >

              <Detail
                icon={
                  Users
                }
                label="최대 참가"
                value={`${tournament.maxParticipants}명`}
              />


              <Detail
                icon={
                  Clock3
                }
                label="시작 시각"
                value={
                  tournament.startTime
                }
              />


              <Detail
                icon={
                  MapPin
                }
                label="장소"
                value={
                  tournament.location
                }
              />


              <Detail
                icon={
                  Ticket
                }
                label="참가 바이인"
                value={`${tournament.buyIn.toLocaleString(
                  'ko-KR',
                )}시드`}
              />

            </div>


            {/* =================================================
                Reservation Button
                ================================================= */}

            <button
              type="button"
              disabled={
                isReserveDisabled
              }
              onClick={
                onReserve
              }
              className={`
                mt-4
                w-full
                rounded-xl
                py-3
                font-semibold
                transition

                ${reserveButtonClassName}
              `}
            >
              {reserveButtonText}
            </button>

          </div>

        </div>

      </article>
    );
  };


type DetailProps = {
  icon:
    React.ComponentType<{
      size?: number;
      className?: string;
    }>;

  label: string;

  value: string;
};


const Detail = ({
  icon: Icon,
  label,
  value,
}: DetailProps) => (

  <div
    className="
      flex
      gap-2
      rounded-lg
      border
      border-gray-200
      p-2.5
    "
  >

    <Icon
      size={17}
      className="
        mt-0.5
        text-gray-500
      "
    />


    <div>

      <p
        className="
          text-xs
          text-gray-500
        "
      >
        {label}
      </p>


      <p
        className="
          font-semibold
          text-gray-900
        "
      >
        {value}
      </p>

    </div>

  </div>
);


export default TournamentCard;